import { useEffect } from 'react'
import { queryKeys } from '@/hooks/query-keys'
import { notificationsApi } from '@/lib/api/notifications.api'
import { useEntityRealtime } from '@/lib/realtime'
import { subscribeToQueryInvalidation } from '@/lib/realtime/shared-query-invalidation'
import { useOrganizationStore } from '@/store/organization.store'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import type { NotificationFeedResponse } from '@scrambl/shared'

export const NOTIFICATION_FEED_QUERY_KEY = queryKeys.notifications.feed()

/**
 * Lane-based notification feed (action required / mentions / activity).
 * Stays live via the existing notification socket events — created/updated/
 * deleted all trigger one debounced refetch — with a slow polling fallback.
 */
export function useNotificationFeed(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options
  const organizationId = useOrganizationStore(
    state => state.currentOrganization?.id,
  )
  const queryClient = useQueryClient()
  const { addEventListener, isConnected } = useEntityRealtime(
    'organization',
    organizationId ?? null,
    { suppressLogging: true },
  )

  const query = useQuery({
    queryKey: NOTIFICATION_FEED_QUERY_KEY,
    queryFn: () => notificationsApi.getFeed(),
    enabled: enabled && !!organizationId,
    staleTime: 20000,
    refetchInterval: isConnected ? 5 * 60_000 : 120_000,
  })

  useEffect(() => {
    if (!enabled || !organizationId) return
    const invalidation = subscribeToQueryInvalidation(
      queryClient,
      organizationId,
      NOTIFICATION_FEED_QUERY_KEY,
    )
    const scheduleInvalidate = invalidation.schedule

    const cleanups = [
      addEventListener('NOTIFICATION_CREATED', scheduleInvalidate),
      addEventListener('NOTIFICATION_UPDATED', scheduleInvalidate),
      addEventListener('NOTIFICATION_DELETED', scheduleInvalidate),
      addEventListener('UNREAD_COUNT_UPDATED', scheduleInvalidate),
    ]
    return () => {
      cleanups.forEach(cleanup => cleanup())
      invalidation.dispose()
    }
  }, [enabled, organizationId, addEventListener, queryClient])

  return query
}

/**
 * THE "waiting on a decision from you" number. Every surface that claims
 * something needs the user — the bell badge, the Today briefing sentence —
 * must read this one figure so they can never disagree. It is the same
 * `actionRequired` lane the popover renders, so the badge and the list behind
 * it are always the same set.
 */
export const selectActionRequiredCount = (
  feed: NotificationFeedResponse | undefined,
): number => feed?.counts.actionRequired ?? 0

/** Hook form of {@link selectActionRequiredCount} for callers without the feed. */
export function useActionRequiredCount(options: { enabled?: boolean } = {}) {
  const { data } = useNotificationFeed(options)
  return selectActionRequiredCount(data)
}
